import { useCallback, useMemo } from 'react';
import type { ComponentType } from 'react';
import type { Row, Table as TanstackTable } from '@tanstack/react-table';
import type { Customer } from '@/services/db/customer/entity';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isUnknownPeopleTotal } from '@/services/db/customer/entity';
import { formatNumber } from '@/utils/number';
import plur from 'plur';
import { isEmptyString } from '@/utils/string';
import { createPeopleColumns } from './people-table-columns';
import { HiddenResultsEmptyState, NoFiltersEmptyState } from './people-table-empty-states';
import type { PeopleTableProps } from './people-table';

const EMPTY_PENDING_DELETION: ReadonlySet<string> = new Set();

/** The shape `DataTable` renders its `emptyStateComponent` with. */
type PeopleEmptyStateComponent = ComponentType<{ table: TanstackTable<Customer> }>;

/**
 * Builds the memoized `DataTable` inputs for `PeopleTable`: row data, stable
 * column defs, the selectability predicate, the pagination config, the
 * empty-state pair (component vs config, with the loading → hidden →
 * no-filters → no-match precedence), and the derived selection copy.
 */
export function usePeopleTable(props: PeopleTableProps) {
  const taxonomy = useProductTaxonomy();
  const {
    people,
    isLoading,
    pendingDeletionIds = EMPTY_PENDING_DELETION,
    allAttributes,
    pagination,
    allSelected,
    totalCount,
    selectedCount,
    hasFilters,
    hasHiddenResults,
    email = '',
    shouldRedactCreatedAt = false,
    addPeopleAction,
  } = props;

  const data = useMemo(() => [...(people ?? [])], [people]);

  const columns = useMemo(
    () => createPeopleColumns({ allAttributes, shouldRedactCreatedAt }),
    [allAttributes, shouldRedactCreatedAt],
  );

  const getRowCanSelect = useCallback(
    (row: Row<Customer>) => !pendingDeletionIds.has(row.original.id),
    [pendingDeletionIds],
  );

  const paginationConfig = useMemo(() => ({ ...pagination, isManual: true }), [pagination]);

  const emptyStateComponent = useMemo((): PeopleEmptyStateComponent | undefined => {
    if (isLoading === true) return undefined;
    if (hasHiddenResults) return HiddenResultsEmptyState;
    if (!hasFilters) {
      function PeopleNoFiltersEmptyState() {
        return <NoFiltersEmptyState action={addPeopleAction} />;
      }
      return PeopleNoFiltersEmptyState;
    }
    return undefined;
  }, [isLoading, hasHiddenResults, hasFilters, addPeopleAction]);

  const emptyState = useMemo(() => {
    if (hasFilters && !hasHiddenResults && isLoading !== true) {
      const people = taxonomy.term('people.plural');
      return {
        title: isEmptyString(email) ? `No ${people} matching your filters` : `No ${people} matching your search`,
        icon: 'person' as const,
      };
    }
    return undefined;
  }, [hasFilters, hasHiddenResults, isLoading, email, taxonomy]);

  const unknownTotal = isUnknownPeopleTotal(totalCount);
  const hasAnySelection = allSelected || selectedCount > 0;
  const peopleToSelectCount = `${formatNumber(totalCount)} ${plur(taxonomy.term('people.singular'), totalCount)}`;

  return {
    data,
    columns,
    getRowCanSelect,
    paginationConfig,
    emptyState,
    emptyStateComponent,
    unknownTotal,
    hasAnySelection,
    peopleToSelectCount,
    peopleSingular: taxonomy.term('people.singular'),
    peoplePlural: taxonomy.term('people.plural'),
    peopleTitlePlural: taxonomy.term('people.titlePlural'),
  };
}
